import { http } from '@/lib/ats-https';
import { useQuery, useMutation } from '@tanstack/react-query';

// API Functions
export const offerDetailsAPI = {
  // Create offer
  createOffer: async ({ payload }) => {
    const response = await http.post('/ats_offers', payload);
    return response;
  },
  // Update offer
  updateOffer: async ({ payload, id }) => {
    const response = await http.put(`/ats_offers/${id}`, payload);
    return response;
  },
  skipApprovalAndGenerateOffer: async ({ id }) => {
    const response = await http.put(
      `/ats_offers/${id}/update_status/return_to_pending_approval`
    );
    return response;
  },
  // Get custom fields for offers
  getCustomFields: async (params) => {
    const response = await http.get('/ats_custom_fields/form_fields', {
      params,
    });
    return response.data;
  },

  // Get offer by ID
  getOfferById: async (id) => {
    const response = await http.get(`/ats_offers/${id}`);
    return response.data;
  },

  // Get compensation currencies
  getCompensationCurrencies: async () => {
    const response = await http.get('/compensations/currencies');
    return response.data;
  },

  // Get ATS offices
  getAtsOffices: async (params = {}) => {
    const response = await http.get('/ats_offices', { params });
    return response.data;
  },

  // Get ATS departments
  getAtsDepartments: async () => {
    const response = await http.get('/ats_departments');
    return response.data?.departments;
  },

  // Get all offer templates
  getAllOfferTemplates: async (params = {}) => {
    const response = await http.get('/ats_offer_templates', { params });
    return response;
  },

  // Get approval flow by entity
  getApprovalsByEntity: async ({ entityId, entityType }) => {
    const response = await http.get(
      `approvals/approval_template?entity_id=${entityId}&type=${entityType}`
    );
    return response.data;
  },

  // Get approval settings
  getApprovalSettings: async () => {
    const response = await http.get('/approvals/settings');
    return response.data;
  },

  // Upload replacement document. For multi-doc offers, documentIndex targets one document.
  uploadReplacement: async ({ id, file, documentIndex }) => {
    const formData = new FormData();
    formData.append('offer_document', file);
    formData.append('name', 'test');
    if (documentIndex !== undefined && documentIndex !== null) {
      formData.append('document_index', documentIndex);
    }
    const response = await http.put(
      `/ats_offers/${id}/upload_replacement`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  },

  // Remove one document from a multi-doc offer (per-offer; the shared template is untouched).
  removeOfferDocument: async ({ id, documentIndex }) => {
    const response = await http.delete(`/ats_offers/${id}/remove_document`, {
      data: { document_index: documentIndex },
    });
    return response.data;
  },

  // Generate offer document
  generateOfferDocument: async ({ id, event }) => {
    const response = await http.put(
      `ats_offers/${id}/update_status/${event}`,
      null
    );
    return response.data;
  },

  sendForApproval: async ({ id, payload = {} }) => {
    const response = await http.put(
      `/ats_offers/${id}/update_status/send_for_approval`,
      payload
    );
    return response;
  },

  // Send offer email
  sendOfferEmail: async ({ id, payload }) => {
    const response = await http.put(`/ats_offers/${id}/send_email`, payload);
    return response;
  },

  // validatePreapproval: async ({ payload, offerId }) => {
  //   const response = await http.post(
  //     `/ats_offers/${offerId}/validate_preapproval`,
  //     payload
  //   );
  //   return response.data;
  // },

  // Poll offer status
  pollOfferById: async (id) => {
    const response = await http.get(`/ats_offers/${id}/poll_status`);
    return response.data;
  },

  // Upload attachment
  uploadAttachment: async ({ id, file }) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await http.put(
      `/ats_offers/${id}/upload_attachment`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response;
  },

  // Delete attachment
  deleteAttachment: async ({ offerId, attachmentId }) => {
    const response = await http.delete(
      `/ats_offers/${offerId}/delete_attachment`,
      { data: { attachment_id: attachmentId } }
    );
    return response;
  },
};

// Query Keys
export const offerDetailsKeys = {
  all: ['offer-details'],
  customFields: (params) => [...offerDetailsKeys.all, 'custom-fields', params],
  offerById: (id) => [...offerDetailsKeys.all, 'offer', id],
  currencies: () => [...offerDetailsKeys.all, 'currencies'],
  offices: (params) => [...offerDetailsKeys.all, 'offices', params],
  departments: () => [...offerDetailsKeys.all, 'departments'],
  templates: (params) => [...offerDetailsKeys.all, 'templates', params],
  approvals: (entityId, entityType) => [
    ...offerDetailsKeys.all,
    'approvals',
    entityId,
    entityType,
  ],
};

// React Query Hooks
export const useGetCustomFieldsQuery = (params, options = {}) => {
  return useQuery({
    queryKey: offerDetailsKeys.customFields(params),
    queryFn: () => offerDetailsAPI.getCustomFields(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useGetOfferByIdQuery = (id, options = {}) => {
  return useQuery({
    queryKey: offerDetailsKeys.offerById(id),
    queryFn: () => offerDetailsAPI.getOfferById(id),
    staleTime: 0,
    enabled: !!id,
    ...options,
  });
};

export const useGetCompensationCurrenciesQuery = (options = {}) => {
  return useQuery({
    queryKey: offerDetailsKeys.currencies(),
    queryFn: offerDetailsAPI.getCompensationCurrencies,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useGetAtsOfficesQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: offerDetailsKeys.offices(params),
    queryFn: () => offerDetailsAPI.getAtsOffices(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useGetAtsDepartmentsQuery = (options = {}) => {
  return useQuery({
    queryKey: offerDetailsKeys.departments(),
    queryFn: offerDetailsAPI.getAtsDepartments,
    staleTime: 3 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useGetAllOfferTemplatesQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: offerDetailsKeys.templates(params),
    queryFn: () => offerDetailsAPI.getAllOfferTemplates(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useGetApprovalsByEntityQuery = (
  { entityId, entityType },
  options = {}
) => {
  return useQuery({
    queryKey: offerDetailsKeys.approvals(entityId, entityType),
    queryFn: () =>
      offerDetailsAPI.getApprovalsByEntity({ entityId, entityType }),
    staleTime: 0,
    enabled: !!entityId && !!entityType,
    ...options,
  });
};

export const useGetApprovalSettingsQuery = (options = {}) => {
  return useQuery({
    queryKey: ['approval-settings'],
    queryFn: () => offerDetailsAPI.getApprovalSettings(),
    staleTime: 0,
    ...options,
  });
};

// Mutation Hooks
export const useUploadReplacementMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.uploadReplacement,
    ...options,
  });
};

export const useGenerateOfferDocumentMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.generateOfferDocument,
    ...options,
  });
};

export const useSendForApprovalMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.sendForApproval,
    ...options,
  });
};

export const useSendOfferEmailMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.sendOfferEmail,
    ...options,
  });
};

// Poll hook with custom interval
export const usePollOfferByIdQuery = (id, options = {}) => {
  return useQuery({
    queryKey: [...offerDetailsKeys.offerById(id), 'poll'],
    queryFn: () => offerDetailsAPI.pollOfferById(id),
    staleTime: 0,
    refetchInterval: (data) => {
      // Stop polling if document is ready or failed
      if (!data) return false;
      const status = data?.offer_status;
      if (
        status === 'pending_document_generation' ||
        status === 'document_generation_in_progress'
      ) {
        return 2000; // Poll every 2 seconds
      }
      return false; // Stop polling
    },
    enabled: !!id,
    ...options,
  });
};

export const useUploadAttachmentMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.uploadAttachment,
    ...options,
  });
};

export const useDeleteAttachmentMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.deleteAttachment,
    ...options,
  });
};

export const useRemoveOfferDocumentMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.removeOfferDocument,
    ...options,
  });
};

// export const useValidatePreapprovalMutation = (options = {}) => {
//   return useMutation({
//     mutationFn: offerDetailsAPI.validatePreapproval,
//     ...options,
//   });
// };
